// ---------------------------------------------------------------------------
// Export/import a complete project as a single downloadable file (.ehdz):
// a portable backup, to move a project between browsers/machines or save it
// outside IndexedDB — unrelated to the real desktop .ehd exporter (see
// CLAUDE.md, still pending).
//
// Format: the same ProjectData that already lives in IndexedDB, serialized
// to JSON (with a replacer/reviver for the two types JSON doesn't support
// natively — Map in EvalHidProject.untypedTables and Float64Array in each
// DailySeries column, see types.ts/dailySeries.ts) and compressed with native
// gzip (CompressionStream/DecompressionStream) — no new library needed. The
// app already requires the File System Access API for "Open project from
// folder" (Chromium/Edge), so requiring CompressionStream (same support,
// plus Safari 16.4+) doesn't add any new browser restriction.
//
// The actual serialize+gzip work (exportProjectSerialize.ts) runs inside
// exportProjectWorker.ts, off the main thread — see that module's own
// header for the real incident this fixes (a multi-second UI freeze on
// "Export copy" for a Duero-scale project). Same fallback-to-same-thread
// pattern as csvWorkerClient.ts: `typeof Worker === "undefined"` in Node
// (tests), so `serializeAndCompress` just runs directly there instead.
// ---------------------------------------------------------------------------
import { migratedProjectData, type ProjectData } from "./storage.ts";
import { sanitizedProjectFileName } from "./fileNaming.ts";
import { TranslatableError } from "../i18n/translate.ts";
import { chunkSeriesColumns } from "./seriesBatching.ts";
import {
  FORMAT,
  FORMAT_VERSION,
  reviver,
  serializeAndCompress,
  type Wrapper,
} from "./exportProjectSerialize.ts";
import type {
  ExportWorkerBatchMessage,
  ExportWorkerFinishMessage,
  ExportWorkerInitMessage,
  ExportWorkerResponse,
} from "./exportProjectWorkerMessages.ts";

/** Every array-typed field of EvalHidProject (types.ts) — `version`/`untypedTables` are checked separately below, everything else here is a plain array. */
const PROJECT_ARRAY_FIELDS = [
  "subBasins",
  "aquifers",
  "outletPoints",
  "zones",
  "modelTypes",
  "parameterDefinitions",
  "parameterValues",
  "stateVariableDefinitions",
  "stateVariableValues",
  "simulationFiles",
  "gisLayers",
  "dateRanges",
  "scenarioElements",
  "unitOptions",
] as const;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A shallow structural check, not a full schema — this is a real trust
 * boundary (`importProject` is reachable from an arbitrary user-uploaded
 * file, Landing.tsx's "Import copy…", and from whatever happens to be
 * sitting in the app's own Drive folder, driveSyncStore.ts's `reconcile`/
 * `resolveConflict`), and until now a file that passed the `format`/
 * `version` check above was trusted as `ProjectData` via a bare cast with
 * zero validation of its own — a missing/malformed field would only
 * surface later, deep inside rendering/engine code, with a far less clear
 * error than "this isn't a valid EvalHid export". Checks that every
 * array-typed field of `EvalHidProject` is actually an array (catches a
 * corrupted/truncated/hand-crafted file, since almost every one of these
 * gets iterated somewhere on load) and that the reviver actually turned
 * `untypedTables`/each series' Float64Array columns back into real
 * instances — NOT a deep validation of every entity's own fields (a full
 * schema for the whole project tree would be far more to maintain than
 * this trust boundary needs).
 */
function isValidProjectData(data: unknown): data is ProjectData {
  if (!isPlainObject(data)) return false;
  if (typeof data.id !== "string") return false;
  if (!isPlainObject(data.outletPointsLayer) || !isPlainObject(data.aquifersLayer)) return false;
  if (data.gisLayer !== null && !isPlainObject(data.gisLayer)) return false;

  const project = data.project;
  if (!isPlainObject(project)) return false;
  if (!PROJECT_ARRAY_FIELDS.every((field) => Array.isArray(project[field]))) return false;
  if (!(project.untypedTables instanceof Map)) return false;

  const series = data.series;
  if (!isPlainObject(series)) return false;
  for (const key of ["precipitation", "etp", "temperature", "pumping"] as const) {
    const s = series[key];
    if (s !== null && (!isPlainObject(s) || !Array.isArray(s.dates))) return false;
  }

  return true;
}

/** Suggested file name from the project's name, without characters that are problematic for the file system. */
export function exportedFileName(projectName: string): string {
  return `${sanitizedProjectFileName(projectName)}.ehdz`;
}

/**
 * Serializes and compresses a complete project into a single downloadable
 * Blob (.ehdz) — via a Worker whenever one's available (every real browser;
 * see exportProjectWorker.ts for why), falling back to running
 * `serializeAndCompress` directly on this same thread only where `Worker`
 * doesn't exist at all (Node/tests) — same pattern as
 * csvWorkerClient.ts/csvWorker.ts.
 *
 * Sends the batched series in small `postMessage` chunks (`init`, then
 * `batch` × N, then `finish` — see exportProjectWorkerMessages.ts's own header for the crash
 * this replaced) instead of the whole `ProjectData` in one shot — the
 * fallback path above (no `Worker` at all) doesn't need any of this: it's
 * a single function call on this same thread, never a `postMessage`, so
 * there's nothing to clone/duplicate in the first place.
 */
export async function exportProject(name: string, data: ProjectData): Promise<Blob> {
  if (typeof Worker === "undefined") {
    const compressed = await serializeAndCompress(name, data);
    return new Blob([compressed], { type: "application/gzip" });
  }
  const { series, ...dataWithoutSeries } = data;
  // Each series type chunked independently (see seriesBatching.ts's own
  // header for why NOT by matching sub-basin code against column name) —
  // they can have different column counts, so batch `i` of one doesn't
  // necessarily line up with batch `i` of another; the loop below just
  // sends whichever of the three still has a chunk at each index.
  const precipChunks = chunkSeriesColumns(series.precipitation);
  const etpChunks = chunkSeriesColumns(series.etp);
  const tempChunks = chunkSeriesColumns(series.temperature);
  const totalBatches = Math.max(precipChunks.length, etpChunks.length, tempChunks.length);

  const compressed = await new Promise<ArrayBuffer>((resolve, reject) => {
    const worker = new Worker(new URL("./exportProjectWorker.ts", import.meta.url), {
      type: "module",
    });
    // Cleared on whichever handler fires first — same reasoning as
    // csvWorkerClient.ts's own `done()`, see its comment.
    function done() {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
    }
    worker.onmessage = (event: MessageEvent<ExportWorkerResponse>) => {
      const response = event.data;
      if (response.ok) {
        resolve(response.compressed);
      } else {
        reject(new Error(response.error));
      }
      done();
    };
    worker.onerror = (event) => {
      reject(new Error(event.message || "Error exporting the project in the worker."));
      done();
    };

    const initMessage: ExportWorkerInitMessage = {
      type: "init",
      name,
      dataWithoutSeries: dataWithoutSeries as Omit<ProjectData, "series">,
      pumping: series.pumping,
      dates: {
        ...(series.precipitation ? { precipitation: series.precipitation.dates } : {}),
        ...(series.etp ? { etp: series.etp.dates } : {}),
        ...(series.temperature ? { temperature: series.temperature.dates } : {}),
      },
      totalBatches,
    };
    worker.postMessage(initMessage);

    for (let i = 0; i < totalBatches; i++) {
      const slices: ExportWorkerBatchMessage["slices"] = {};
      if (precipChunks[i]) slices.precipitation = precipChunks[i];
      if (etpChunks[i]) slices.etp = etpChunks[i];
      if (tempChunks[i]) slices.temperature = tempChunks[i];
      const batchMessage: ExportWorkerBatchMessage = { type: "batch", batchIndex: i, slices };
      // Every Float64Array here is already this batch's OWN fresh copy
      // (chunkSeriesColumns's own `.slice()`), never the live project's —
      // safe to hand off as a transferable (a zero-copy move) instead of
      // letting postMessage clone it a SECOND time.
      const transfer = Object.values(slices).flatMap((s) => s.values.map((v) => v.buffer));
      worker.postMessage(batchMessage, transfer);
    }

    const finishMessage: ExportWorkerFinishMessage = { type: "finish" };
    worker.postMessage(finishMessage);
  });
  return new Blob([compressed], { type: "application/gzip" });
}

/**
 * Decompresses and validates a .ehdz file — throws if it's not a valid
 * export from this app or from an incompatible future version. Runs the
 * data through `migratedProjectData` (storage.ts) before returning it, same
 * as IndexedDB's own DB_VERSION upgrade does for an already-saved project —
 * an old export can just as easily carry pre-fix data (e.g. the old
 * `SubBasin.outletCode` `0` sentinel), and there's no separate "import
 * version" to gate that on here.
 */
export async function importProject(file: File): Promise<{ name: string; data: ProjectData }> {
  let json: string;
  try {
    const decompressedStream = file.stream().pipeThrough(new DecompressionStream("gzip"));
    const decompressed = await new Response(decompressedStream).arrayBuffer();
    json = new TextDecoder().decode(decompressed);
  } catch {
    throw new TranslatableError("exportProject.decompressError", { name: file.name });
  }

  let wrapper: unknown;
  try {
    wrapper = JSON.parse(json, reviver);
  } catch {
    throw new TranslatableError("exportProject.readInternalFormatError", { name: file.name });
  }

  if (typeof wrapper !== "object" || wrapper === null || (wrapper as Wrapper).format !== FORMAT) {
    throw new TranslatableError("exportProject.notExportedError", { name: file.name });
  }
  const { version, name, data } = wrapper as Wrapper;
  if (version > FORMAT_VERSION) {
    throw new TranslatableError("exportProject.newerVersionError", { name: file.name });
  }
  if (!isValidProjectData(data)) {
    throw new TranslatableError("exportProject.corruptedDataError", { name: file.name });
  }
  return { name, data: migratedProjectData(data) };
}
